import React from 'react';
import {
  Clock,
  MapPin,
  AlertTriangle,
  Bus,
  Crosshair,
  User,
} from 'lucide-react';
import { GpsLocationDto } from '@area-restrita/api-client/model/gpsLocationDto';

import { cn } from '@/lib/utils';

interface IProps {
  className?: string;
  location: GpsLocationDto | null;
  distanceToStop?: number; // em metros
  routeName?: string;
  driverName?: string;
  isRecent: boolean;
  estimatedArrival: string;
  lastUpdate: string;
  busStatus: {
    text: string;
    color: string;
  };
  details?: React.ReactNode;

  hasUserLocation: boolean;
  onRequestUserLocation: () => void;
}

const BusTrackingCardView: React.FC<IProps> = ({
  location,
  className,
  distanceToStop,
  routeName,
  driverName,
  isRecent,
  estimatedArrival,
  lastUpdate,
  busStatus,
  details,
  hasUserLocation,
  onRequestUserLocation,
}) => (
  <div
    className={cn(
      'bg-white rounded-lg shadow-lg p-4 w-full border border-gray-200',
      className,
    )}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center">
        <Bus className="h-6 w-6 text-gray-700 mr-2" />
        <h3 className="font-semibold text-gray-800">{routeName}</h3>
      </div>
      <div className="flex items-center">
        <span
          className={cn(
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
            isRecent
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800',
          )}
        >
          {isRecent ? 'Atualizado' : 'Desatualizado'}
        </span>
      </div>
    </div>

    {details && <div className="mb-3">{details}</div>}

    <div className="grid grid-cols-2 gap-3 mb-3">
      <div className="bg-gray-50 rounded-md p-3">
        <div className="text-sm text-gray-500 mb-1">Prefixo</div>
        <div className="font-medium">{location?.prefix ?? '----'}</div>
      </div>

      <div className="bg-gray-50 rounded-md p-3">
        <div className="text-sm text-gray-500 mb-1">Velocidade</div>
        <div className="font-medium">
          {location?.speed ? `${location.speed} km/h` : 'Indisponível'}
        </div>
      </div>
    </div>

    {driverName && (
      <div className="bg-gray-50 rounded-md p-3 mb-3">
        <div className="flex items-center">
          <User className="h-4 w-4 text-gray-500 mr-2" />
          <div>
            <div className="text-sm text-gray-500 mb-1">Motorista</div>
            <div className="font-medium text-gray-800">{driverName}</div>
          </div>
        </div>
      </div>
    )}

    <div className="flex items-center mb-3">
      <div className={cn('w-3 h-3 rounded-full mr-2', busStatus.color)}></div>
      <span className="text-sm font-medium">{busStatus.text}</span>
    </div>

    <div className="bg-blue-50 rounded-md p-3 mb-3">
      <div className="flex items-center mb-1">
        <Clock className="h-4 w-4 text-blue-500 mr-1" />
        <div className="text-sm text-blue-700">Tempo estimado de chegada</div>
      </div>
      <div className="font-semibold text-blue-900">{estimatedArrival}</div>
    </div>

    <div className="flex items-center justify-between text-xs text-gray-500">
      <div className="flex items-center">
        <MapPin className="h-3 w-3 mr-1" />
        {distanceToStop ? (
          <span>{Math.round(distanceToStop / 100) / 10} km de você</span>
        ) : (
          <span>Indisponível</span>
        )}
      </div>
      <div className="flex items-center">
        <Clock className="h-3 w-3 mr-1" />
        <span>Atualizado {lastUpdate}</span>
      </div>
    </div>

    {!isRecent && (
      <div className="mt-3 flex items-start bg-amber-50 p-2 rounded-md text-xs text-amber-800">
        <AlertTriangle className="h-4 w-4 text-amber-500 mr-1 flex-shrink-0 mt-0.5" />
        <p>
          Os dados de localização estão desatualizados. As informações exibidas
          podem não refletir a posição atual do veículo.
        </p>
      </div>
    )}

    {!hasUserLocation && (
      <button
        onClick={onRequestUserLocation}
        className="mt-3 flex w-full items-center justify-center bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition"
      >
        <Crosshair className="h-4 w-4 mr-2" />
        Solicitar Localização
      </button>
    )}
  </div>
);

export default BusTrackingCardView;
